/**
 * Пути API-источников данных для виджетов дашборда.
 *
 * ПУТИ ЗДЕСЬ — БЕЗ `/api` (AD-265). Базовый адрес клиента уже оканчивается на `/api`
 * (`appConfig.API_BASE_URL`, по умолчанию `http://localhost:3001/api`), поэтому значение
 * вида `/api/teams/my` складывалось в `…/api/api/teams/my` и давало 404 на каждом
 * обращении. Инвариант закреплён тестом `dashboard-endpoints.spec.ts`: ни одно значение
 * не начинается с `/api/`.
 *
 * ЧЕГО ЗДЕСЬ НЕТ. Часть значений указывает на маршруты, которых на сервере нет вовсе
 * (сверка с `openapi.json` 15.09.2026) — они помечены `МАРШРУТА НЕТ`. Сегодня это ни на
 * что не влияет: такие значения лежат в поле `dataSource` конфигов виджетов
 * (`RoleDashboardLayout.tsx`), а `Widget` это поле не читает и запросов по нему не шлёт.
 * Снятие префикса их не оживляет и не ломает. Отдельная находка — `AD-294`.
 */
export const DASHBOARD_ENDPOINTS = {
  ORGANIZER: {
    METRICS: "/organizer/metrics",
    EVENTS_RECENT: "/organizer/events/recent",
    TASKS_PENDING: "/organizer/tasks/pending",
    BUDGET: "/organizer/budget", // МАРШРУТА НЕТ (AD-294)
    BUDGET_OVERVIEW: "/organizer/budget/overview",
  },
  SUPPLIER: {
    METRICS: "/supplier/metrics",
    ORDERS: "/supplier/orders",
    ORDERS_ACTIVE: "/supplier/orders/active", // МАРШРУТА НЕТ (AD-294)
    REVIEWS: "/supplier/reviews",
    ANALYTICS_REVENUE: "/supplier/analytics/revenue", // МАРШРУТА НЕТ (AD-294)
  },
  PARTICIPANT: {
    METRICS: "/participant/metrics",
    EVENTS_UPCOMING: "/participant/events/upcoming",
    ACHIEVEMENTS: "/participant/achievements", // МАРШРУТА НЕТ (AD-294)
    RECOMMENDATIONS: "/participant/recommendations",
  },
  SPEAKER: {
    METRICS: "/speaker/metrics",
    SESSIONS: "/speaker/sessions", // МАРШРУТА НЕТ (AD-294)
    PRESENTATIONS: "/speaker/presentations",
    SCHEDULE: "/speaker/schedule",
    MATERIALS: "/speaker/materials",
    AUDIENCE: "/speaker/audience", // МАРШРУТА НЕТ (AD-294)
    FEEDBACK: "/speaker/feedback", // МАРШРУТА НЕТ (AD-294)
  },
  ADMIN: {
    METRICS: "/admin/metrics", // МАРШРУТА НЕТ (AD-294)
    SYSTEM_METRICS: "/admin/system/metrics",
    LOGS: "/admin/logs",
    USERS: "/admin/users",
    SECURITY: "/admin/security", // МАРШРУТА НЕТ (AD-294)
    ANALYTICS: "/admin/analytics",
  },
  MANAGER: {
    TEAM: "/manager/team", // МАРШРУТА НЕТ (AD-294)
    PROJECTS: "/manager/projects", // МАРШРУТА НЕТ (AD-294)
    ANALYTICS: "/manager/analytics", // МАРШРУТА НЕТ (AD-294)
    PLATFORM_METRICS: "/manager/platform/metrics",
    EVENTS: "/manager/events", // МАРШРУТА НЕТ (AD-294)
    ANALYTICS_USERS: "/manager/analytics/users",
  },
  DEFAULT: {
    WELCOME: "/default/welcome", // МАРШРУТА НЕТ (AD-294)
  },
  TEAMS: {
    MY: "/teams/my",
    MEMBERS: (teamId: string) => `/teams/${teamId}/members`,
    /** Настоящий маршрут инсайтов — под `analytics`, а не `teams/:id/insights`. */
    INSIGHTS: (teamId: string) => `/teams/${teamId}/analytics/insights`,
    INSIGHT_LIKE: (teamId: string, insightId: string) =>
      `/teams/${teamId}/analytics/insights/${insightId}/like`,
    INSIGHT_DISLIKE: (teamId: string, insightId: string) =>
      `/teams/${teamId}/analytics/insights/${insightId}/dislike`,
    INSIGHT: (teamId: string, insightId: string) =>
      `/teams/${teamId}/analytics/insights/${insightId}`,
    INSIGHT_COMMENTS: (teamId: string, insightId: string) =>
      `/teams/${teamId}/analytics/insights/${insightId}/comments`,
    INSIGHTS_STATISTICS: (teamId: string) =>
      `/teams/${teamId}/analytics/statistics`,
    PRIMARY_TEAM: "/users/me/primary-team",
  },
} as const;
